<template>
  <main class="container">
    <h1 class="page-title" data-testid="page-title">Tell Us About You</h1>

    <BackButton class="back-link" />

    <form
      class="form personal-form"
      novalidate
      @submit.prevent="formSubmit"
      autocomplete="on"
    >
      <fieldset class="form__fieldset">
        <div
          class="form-item"
          :class="{ '--has-error': $v.personalDetails.leadType.$error }"
        >
          <label class="form__label">
            <span class="form__label-text">Which Describes You?</span>
            <FormError
              v-if="$v.personalDetails.leadType.$error"
              data-testid="lead-type-error"
              msg="Please select an option"
            />
          </label>
          <div class="form__radiobuttons">
            <label
              for="studentLead"
              data-testid="student-lead"
              :class="{ active: leadType === 'Student' }"
            >
              <input
                type="radio"
                id="studentLead"
                name="leadType"
                v-model="leadType"
                value="Student"
                required
              />
              Student
            </label>
            <label
              for="notStudentLead"
              data-testid="notStudentLead"
              :class="{ active: leadType === 'Not a Student' }"
            >
              <input
                type="radio"
                id="notStudentLead"
                name="leadType"
                v-model="leadType"
                value="Not a Student"
                required
              />
              Not A Student
            </label>
          </div>
        </div>
        <div
          class="form-item"
          :class="{ '--has-error': $v.personalDetails.firstname.$error }"
        >
          <label class="form__label" for="fname">
            <span class="form__label-text">First name</span>
            <FormError
              v-if="
                $v.personalDetails.firstname.$error &&
                  !$v.personalDetails.firstname.required
              "
              msg="First name required"
              data-testid="first-name-error"
            />
          </label>
          <input
            class="form__input"
            type="text"
            id="fname"
            data-testid="first-name"
            name="fname"
            autocomplete="given-name"
            v-model.trim="firstname"
            @blur="capitaliseName"
          />
        </div>

        <div
          class="form-item"
          :class="{ '--has-error': $v.personalDetails.lastname.$error }"
        >
          <label class="form__label" for="lname">
            <span class="form__label-text">Last name</span>
            <FormError
              v-if="
                $v.personalDetails.lastname.$error &&
                  !$v.personalDetails.lastname.required
              "
              msg="Last name required"
              data-testid="last-name-error"
            />
          </label>
          <input
            class="form__input"
            type="text"
            id="lname"
            data-testid="last-name"
            name="lname"
            required
            autocomplete="family-name"
            v-model.trim="lastname"
            @blur="capitaliseName"
          />
        </div>

        <email-input
          name="email"
          label="Email address"
          v-model.trim="email"
          :v="$v.personalDetails.email"
        />

        <div
          class="form-item"
          :class="{ '--has-error': $v.personalDetails.tel.$error }"
        >
          <label class="form__label" for="tel">
            <span class="form__label-text">Mobile number</span>
            <FormError
              v-if="
                $v.personalDetails.tel.$error &&
                  !$v.personalDetails.tel.required
              "
              msg="Mobile number required"
              data-testid="phone-number-required-error"
            />
            <FormError
              v-if="
                $v.personalDetails.tel.$error &&
                  !$v.personalDetails.tel.phoneNumber
              "
              msg="Mobile number is invalid"
              data-testid="phone-number-format-error"
            />
          </label>
          <input
            class="form__input"
            type="tel"
            id="tel"
            data-testid="mobile-number"
            name="tel"
            required
            autocomplete="tel"
            v-model.trim="tel"
          />
        </div>
      </fieldset>

      <button
        class="btn btn--big btn--full-width"
        data-testid="next-button"
        type="submit"
        @click="trackSubmitBtn"
      >
        Next
      </button>

      <InfoBox>
        <template v-slot:image>
          <CustomerIcon width="23px" height="21px" />
        </template>
        <template v-slot:title>
          Every Housemate Is Equal
        </template>
        <template v-slot>
          Your housemates will add their details later and we won’t set up a
          thing until everyone has joined.
        </template>
      </InfoBox>
    </form>
  </main>
</template>

<script>
import { mapMutations } from "vuex";
import { email, required } from "vuelidate/lib/validators";
import phoneNumber from "@/utility/phoneNumberValidation";
import FormError from "@/components/forms/FormError";
import InfoBox from "@/components/InfoBox";
import CustomerIcon from "@/assets/svg/icon-customer.svg";
import EmailInput from "@/components/EmailInput";

export default {
  name: "PersonalDetails",
  computed: {
    personalDetails() {
      return this.$store.state.personalDetails;
    },
    leadType: {
      get() {
        return this.personalDetails.leadType;
      },
      set(value) {
        this.$store.commit("SET_PERSONAL_DETAILS_PROP", { leadType: value });
      },
    },
    firstname: {
      get() {
        return this.personalDetails.firstname;
      },
      set(value) {
        this.$store.commit("SET_PERSONAL_DETAILS_PROP", { firstname: value });
      },
    },
    lastname: {
      get() {
        return this.personalDetails.lastname;
      },
      set(value) {
        this.$store.commit("SET_PERSONAL_DETAILS_PROP", { lastname: value });
      },
    },
    email: {
      get() {
        return this.personalDetails.email;
      },
      set(value) {
        this.$store.commit("SET_PERSONAL_DETAILS_PROP", { email: value });
      },
    },
    tel: {
      get() {
        return this.personalDetails.tel;
      },
      set(value) {
        this.$store.commit("SET_PERSONAL_DETAILS_PROP", { tel: value });
      },
    },
  },
  methods: {
    ...mapMutations({
      capitaliseName: "CAPITALISE_NAME",
      showBackBtn: "SHOW_BACK_BTN",
    }),
    formSubmit() {
      this.$v.$touch();

      if (!this.$v.$invalid) {
        this.capitaliseName();
        this.$router.push("/tenancy");
      }
    },
    trackSubmitBtn() {
      this.trackGoogleEvent(
        "Click Next Personal",
        "Create Account",
        "Confirm personal details",
      );
      this.trackFacebookEvent("Click Next Personal");
    },
  },
  created() {
    this.showBackBtn();
  },
  validations() {
    return {
      personalDetails: {
        leadType: {
          required,
        },
        firstname: {
          required,
        },
        lastname: {
          required,
        },
        email: {
          required,
          email,
        },
        tel: {
          required,
          phoneNumber,
        },
      },
    };
  },
  components: {
    EmailInput,
    CustomerIcon,
    InfoBox,
    FormError,
  },
};
</script>

<style lang="scss" scoped>
.container {
  max-width: 60rem;
}

.form-checkbox {
  width: $container-button-width;
  margin: auto;

  @include media("<medium") {
    width: 100%;
  }
}

.--has-error {
  .form__radiobuttons label {
    border-color: $error;
  }
}

.form__radiobuttons {
  display: flex;

  label {
    cursor: pointer;
    border: 2px solid #e2e2e2;
    border-radius: 10px;
    width: 50%;
    padding: 14px;
    text-align: center;
    display: grid;
    place-items: center;

    &:first-of-type {
      margin-right: 22px;
    }

    &.active {
      border-color: black;
    }

    & input[type="radio"] {
      display: none;
    }
  }
}
</style>
